export const industriesData = {
  'healthcare': {
    id: 'healthcare',
    title: 'Healthcare & Clinics',
    icon: 'Activity',
    tagline: 'Streamline patient bookings, clinic schedules, and records securely.',
    overview: 'In healthcare, operational efficiency directly impacts patient care. We help clinics, doctors, and hospitals digitize patient scheduling, manage appointment queues, and securely store records. By automating reminders and clinic administration, we reduce waiting times, eliminate booking errors, and let your staff focus on caring for patients.',
    challenges: [
      'Double-booked appointments or high patient cancellation rates because of manual scheduling systems.',
      'Staff spending hours on the phone calling patients to confirm times or reschedule slots.',
      'Paper records or scattered spreadsheets that make retrieving patient details slow and insecure.'
    ],
    digitalTransformations: [
      'Patient Appointment Booking: A secure online portal where patients can see real-time doctor availability and book slots directly.',
      'Automated Reminders: SMS and email notifications that auto-remind patients of upcoming visits, reducing no-shows.',
      'Secure Clinic Portals: Lightweight databases for staff to manage queue statuses, practitioner notes, and patient files safely.'
    ],
    benefits: [
      'Reduce missed appointments by up to 50% using automatic patient reminder triggers.',
      'Saves hours of receptionist phone time each day by shifting bookings online.',
      'Secure, paperless patient record retrieval in seconds.'
    ],
    relatedServices: ['custom-software', 'mobile-apps', 'ai-automation', 'support-maintenance']
  },
  'retail': {
    id: 'retail',
    title: 'Retail & Shops',
    icon: 'ShoppingCart',
    tagline: 'Modern inventory management, billing, and custom e-commerce stores.',
    overview: 'Whether you run a local gift shop, a jewelry store, or a supermarket, keeping track of stock and sales is vital. We build inventory management systems, barcode-enabled billing software, and custom e-commerce stores that help you sell online, track stock levels in real time, and streamline checkouts.',
    challenges: [
      'Losing sales because you accidentally ran out of stock or could not track inventory across locations.',
      'Slow checkout lines during peak hours because of complicated, legacy billing systems.',
      'Struggling to manage and sync online store inventory with physical shop stock.'
    ],
    digitalTransformations: [
      'Inventory Sync Systems: Real-time databases that alert you when stock is low and auto-update item counts.',
      'Fast Billing Solutions: Lightweight POS web software with barcode scanner support for quick transactions.',
      'Custom E-commerce Stores: Independent online storefronts built without slow website builders, optimized to load in milliseconds.'
    ],
    benefits: [
      'Always know stock counts across all locations, preventing out-of-stock losses.',
      'Shorter wait times for in-store customers with fast, easy-to-use billing screens.',
      'Grow your sales by opening a custom, fast-loading online store.'
    ],
    relatedServices: ['custom-software', 'web-apps', 'mobile-apps', 'website-seo']
  },
  'education': {
    id: 'education',
    title: 'Educational Institutions',
    icon: 'GraduationCap',
    tagline: 'Lightweight school management, attendance logs, and student portals.',
    overview: 'Schools, colleges, and training centers need smooth operations to deliver great education. We develop student information systems, automated attendance trackers, fee collection portals, and online student zones. We make school management simple without the heavy bloat of legacy education packages.',
    challenges: [
      'Teachers spending valuable class time manually taking roll calls and compiling weekly sheets.',
      'Complicated, slow methods for parents to pay fees and view academic progress updates.',
      'Scattered study materials, announcements, and timetables causing student confusion.'
    ],
    digitalTransformations: [
      'Student Management Portals: Centralized portals for timetables, report cards, and announcements.',
      'Automated Attendance: Digital sheets where teachers log attendance in three clicks, auto-syncing with admin records.',
      'Digital Fee Portals: Safe payment links integrated directly into parent accounts with automatic receipt generation.'
    ],
    benefits: [
      'Eliminate manual admin reports, giving teachers more time back for classrooms.',
      'Higher parent satisfaction through transparent, simple portals and updates.',
      'Secure payment processing with automated invoicing.'
    ],
    relatedServices: ['custom-software', 'web-apps', 'design-branding']
  },
  'restaurant': {
    id: 'restaurant',
    title: 'Restaurants & Cafes',
    icon: 'Coffee',
    tagline: 'Automated order systems, digital menus, and local search visibility.',
    overview: 'In hospitality, margins are tight and speed is everything. We build custom restaurant management systems, digital QR menus, and automated table booking applications. We also optimize your local search presence so hungry customers find you on Google Maps first.',
    challenges: [
      'Losing phone orders because staff are too busy during dinner rush hours to answer calls.',
      'Printing expensive physical menus every time a price or item change is needed.',
      'Being invisible locally on Google Search and Maps when people look for food nearby.'
    ],
    digitalTransformations: [
      'QR Code Ordering & Menus: Digital menus customers can view on their phones, allowing instant order submissions.',
      'Local Maps Ranking: High-level Google Business profile setup and review systems to boost local search rankings.',
      'Kitchen Order Screens: Simple tablet screens for chefs to instantly see incoming orders in sequence.'
    ],
    benefits: [
      'Eliminate menu printing costs and update item prices in seconds online.',
      'Increase local foot traffic by ranking high on Google Maps.',
      'Faster order taking and fewer communication errors between tables and kitchens.'
    ],
    relatedServices: ['mobile-apps', 'website-seo', 'ai-automation', 'design-branding']
  },
  'hotel': {
    id: 'hotel',
    title: 'Hotels & Hospitality',
    icon: 'Bed',
    tagline: 'Custom booking engines, room service portals, and guest systems.',
    overview: 'Maximize direct bookings and delight guests. We build direct room booking engines that bypass third-party OTA commissions, guest management systems, and web-based room service portals for instant requests.',
    challenges: [
      'Paying massive commission percentages to travel websites for room bookings.',
      'Complicated guest booking steps that cause travelers to abandon reservations.',
      'Front desk staff overwhelmed with routine service requests like extra towels or food orders.'
    ],
    digitalTransformations: [
      'Direct Booking Engine: Simple, calendar-based booking system integrated directly into your website.',
      'Digital Room Service: In-room QR codes guest can scan to order food, request housekeeping, or check out.',
      'WhatsApp Updates: Automatic booking updates and check-in links sent directly to guests\' phones.'
    ],
    benefits: [
      'Save thousands of rupees in commissions by driving direct website bookings.',
      'Improve staff response times for room service with automated order routing.',
      'A seamless, modern check-in experience that wins positive online reviews.'
    ],
    relatedServices: ['custom-software', 'web-apps', 'ai-automation', 'website-seo']
  },
  'real-estate': {
    id: 'real-estate',
    title: 'Real Estate Companies',
    icon: 'Home',
    tagline: 'Modern property listing search, customer CRM, and lead capture.',
    overview: 'Make it easy for buyers and renters to find their dream property. We build property listing platforms with advanced location filters, client lead-management systems (CRMs), and automated follow-up tools that help agents close deals faster.',
    challenges: [
      'Property listings loading slowly or filtering poorly on mobile, driving prospects away.',
      'Forgetting to follow up with hot buyer leads because details are scattered in notebook logs.',
      'Wasting time manually sending the same property catalogs to every prospective client.'
    ],
    digitalTransformations: [
      'Advanced Listing Search: Fast-loading, map-integrated property filters for area, budget, and size.',
      'Agent CRM Hub: An easy-to-use admin portal to track client preferences, notes, and call schedules.',
      'Automated property alerts: Scheduled email lists matching a customer\'s search criteria automatically.'
    ],
    benefits: [
      'Capture and convert more leads with a fast property search layout.',
      'Agents work smarter by having all client preferences logged in a central place.',
      'Send immediate, automated property details when a lead requests info.'
    ],
    relatedServices: ['web-apps', 'website-seo', 'design-branding', 'custom-software']
  },
  'construction': {
    id: 'construction',
    title: 'Construction & Contractors',
    icon: 'HardHat',
    tagline: 'Showcase your build portfolios and attract local commercial projects.',
    overview: 'Build trust with prospective clients by showcasing your craftsmanship online. We develop professional portfolio websites, material tracking logs, and local search optimization setups for contractors, builders, and construction firms.',
    challenges: [
      'Losing commercial tenders because you lack a professional digital portfolio of past builds.',
      'Missing local service calls because Google searchers cannot find your business contact info.',
      'Poor coordination between site foremen and office staff regarding material usage logs.'
    ],
    digitalTransformations: [
      'High-Resolution Gallery: Modern, fast portfolios displaying past projects by category.',
      'Local SEO Booster: Optimization of business listings so you show up for local construction searches.',
      'Simple Field Logs: Mobile-friendly web forms for site managers to update office staff on project progress.'
    ],
    benefits: [
      'Win bigger contracts by demonstrating your capabilities with a high-end portfolio.',
      'Receive more phone calls from local clients seeking builders.',
      'Clearer logs of construction progress and material updates.'
    ],
    relatedServices: ['website-seo', 'web-apps', 'support-maintenance']
  },
  'manufacturing': {
    id: 'manufacturing',
    title: 'Manufacturing & Factories',
    icon: 'Factory',
    tagline: 'Track raw materials, batch output logs, and machine schedules.',
    overview: 'Eliminate manual paperwork from your production floors. We design lightweight batch logging software, raw material trackers, and equipment maintenance organizers that keep your factory floor running smoothly.',
    challenges: [
      'Batch details and material usage written on paper sheets that easily get lost or misfiled.',
      'Unplanned machine breakdowns because maintenance dates were not tracked or alert-reminded.',
      'Office managers unaware of real-time production counts, delaying client shipment answers.'
    ],
    digitalTransformations: [
      'Production Trackers: Simple digital panels to log batch numbers, operator names, and outputs.',
      'Maintenance Calendars: Uptime trackers that auto-remind managers of upcoming machine service requirements.',
      'Material Alert Logs: Automatic inventory counts showing raw stock usage.'
    ],
    benefits: [
      'Clean digital records for compliance checks and quality control audits.',
      'Prevent expensive machine downtime with automated service reminders.',
      'Real-time output views, allowing precise delivery answers for clients.'
    ],
    relatedServices: ['custom-software', 'web-apps', 'support-maintenance']
  },
  'travel': {
    id: 'travel',
    title: 'Travel & Tour Agencies',
    icon: 'Compass',
    tagline: 'Interactive package bookings, travel itineraries, and lead routes.',
    overview: 'Make travel bookings as smooth as a holiday. We build interactive travel website platforms, tour package booking engines, and inquiry planners that help travelers choose and secure itineraries online.',
    challenges: [
      'Spending hours manually drafting and emailing custom trip itineraries back and forth.',
      'Inability to accept package deposits or secure online bookings.',
      'Customers bouncing from your site because package descriptions load slowly on mobile.'
    ],
    digitalTransformations: [
      'Itinerary Builder: Central tool to create, modify, and present beautiful travel packages.',
      'Package Booking Engine: Secure deposit booking forms with integrated payment links.',
      'Inquiry Planners: Direct forms that capture group travel preferences (budget, destination, days).'
    ],
    benefits: [
      'Dramatically cut the time needed to finalize travel bookings with customers.',
      'Convert immediate intent by accepting direct online deposits.',
      'Fast, mobile-friendly itinerary sheets that travelers can easily read on the go.'
    ],
    relatedServices: ['website-seo', 'mobile-apps', 'design-branding', 'ai-automation']
  },
  'professional-services': {
    id: 'professional-services',
    title: 'Professional Consultants',
    icon: 'Briefcase',
    tagline: 'Elevate your online authority, capture leads, and book consulting calls.',
    overview: 'For accountants, lawyers, and business consultants, trust and authority are everything. We develop clean, high-conversion websites that highlight your insights, integrate direct booking tools (like Calendly), and capture inquiries efficiently.',
    challenges: [
      'Losing potential clients because your site looks generic or is hard to navigate.',
      'Back-and-forth emails to find a meeting time that suits you and your client.',
      'Having no simple way to capture client intake data before a consultation call.'
    ],
    digitalTransformations: [
      'Authority Showcase: Professional websites highlighting bios, service offerings, and insights.',
      'Direct Calendar Scheduling: Embedded calendar selectors allowing clients to choose and pay for consultation sessions.',
      'Client Intake Forms: Dynamic, secure forms that gather necessary background info before calls.'
    ],
    benefits: [
      'Establish immediate expertise with a modern website layout.',
      'Completely eliminate scheduling friction with calendar bookings.',
      'Arrive prepared to consulting calls with patient/client data gathered in advance.'
    ],
    relatedServices: ['website-seo', 'ai-automation', 'custom-software', 'support-maintenance']
  }
};
